/**
 * @module config/moduleVisuals
 * @description The ONE place that says how each module looks: its icon and its
 * Vuetify colour name. Every surface that draws a module icon reads from here —
 * the sidebar (config/navGroups.js, and Home.vue which is generated from it),
 * the app bar (AppBar.vue appConfig + per-route titles), the Account page's
 * "Allowed Apps" list, the module landing pages (MarketingHome, HrHome,
 * SettingsHome, FoodCostHome). Maurizio, 2026-09-10: "keep this nice color
 * consistency across all app" — the Account page palette was the reference.
 *
 * Rules:
 *   - Vuetify colour NAMES only ("blue", "teal", "amber-darken-2", …). Never
 *     hex, never custom CSS.
 *   - Every icon here must be registered in plugins/mdiIcons.js (all of these
 *     already were).
 *   - A page INSIDE a module (Warehouse → Stock Ledger, Accounting → Ledger,
 *     Menu → Recipes …) keeps its own feature icon but takes the module colour.
 *   - The two path-less sidebar accordions (Daily / Monthly operations) are jobs,
 *     not modules, and keep their own colours in navGroups.js.
 *
 * Collision decisions (reference = AccountHome APP_CATALOG before this file):
 *   - Sales blue vs Direct Upload blue-darken-2 → Direct Upload = light-blue
 *     (same family on purpose — it is a Sales-gated cashier feature — but a
 *     clearly different shade).
 *   - GL indigo vs Menu indigo-lighten-1 → Accounting takes the Journal teal
 *     (every GL page was already teal in the sidebar and app bar); Menu keeps
 *     indigo-lighten-1.
 *   - "platforms" (the Marketing grant) deep-orange vs Fusion Eat deep-orange →
 *     Marketing = purple (the reference's own "Marketing" row); Fusion Eat keeps
 *     deep-orange, which it already had everywhere. The sidebar's orange
 *     Marketing is dropped (too close to Assets amber-darken-2 anyway).
 *   - HR Payroll green vs Petty Cash green-darken-2 → Payroll is an HR page and
 *     takes HR's cyan-darken-1. (Petty Cash itself was retired 2026-09-23.)
 *   - Reviews amber vs Assets amber-darken-2 → Reviews is a Marketing page and
 *     takes purple; Assets keeps amber-darken-2.
 *   - The "HR and Contacts" sidebar group takes HR's icon and colour (its own
 *     page is /hr); the Contacts child keeps the Contacts red.
 */

export const MODULES = {
  sales: { title: "Sales", icon: "mdi-chart-bar", color: "blue" },
  directUpload: { title: "Direct Upload", icon: "mdi-cash-register", color: "light-blue" },
  // Brown (the Account page's original) read as dead in the sidebar — Maurizio
  // asked for a vivid orange/mandarin (2026-09-10). orange-darken-1 keeps clear
  // of Assets (amber-darken-2, yellower) and Fusion Eat (deep-orange, redder).
  warehouse: { title: "Warehouse", icon: "mdi-warehouse", color: "orange-darken-1" },
  accounting: { title: "Accounting", icon: "mdi-book-open-page-variant", color: "teal" },
  glConfig: { title: "GL Configuration", icon: "mdi-cog", color: "blue-grey" },
  marketing: { title: "Marketing", icon: "mdi-bullhorn", color: "purple" },
  menu: { title: "Menu", icon: "mdi-silverware-fork-knife", color: "indigo-lighten-1" },
  assets: { title: "Assets", icon: "mdi-folder-multiple-image", color: "amber-darken-2" },
  contacts: { title: "Contacts", icon: "mdi-account-box", color: "red-lighten-1" },
  hr: { title: "HR", icon: "mdi-account-group", color: "cyan-darken-1" },
  // Payroll lives inside HR: own icon, HR's colour.
  payroll: { title: "Payroll", icon: "mdi-account-cash", color: "cyan-darken-1" },
  tasks: { title: "Tasks", icon: "mdi-clipboard-check-outline", color: "deep-purple-lighten-1" },
  meals: { title: "Fusion Eat", icon: "mdi-food-outline", color: "deep-orange" },
};

/** Colour name of a module — for `iconColor` / `:color` bindings. */
export const moduleColor = (key) => MODULES[key]?.color;

/** Icon of a module — for group headers and landing-page cards. */
export const moduleIcon = (key) => MODULES[key]?.icon;

/**
 * Grant code (a value of users.allowed_apps) → module key. Used by the Account
 * page to colour each granted app. Retired codes (still held by older accounts)
 * map to the module that replaced them.
 */
export const GRANT_MODULE = {
  sales: "sales",
  "direct-upload": "directUpload",
  pettycash: "accounting", // retired 2026-09-23 — cash is entered in the Journal
  warehouse: "warehouse",
  "warehouse-ops": "warehouse",
  journal: "accounting",
  gl: "accounting",
  "gl-config": "glConfig",
  platforms: "marketing",
  campaigns: "marketing",
  reviews: "marketing",
  marketing: "marketing",
  menu: "menu",
  assets: "assets",
  contacts: "contacts",
  "address-book": "contacts",
  hr: "hr",
  "hr-management": "hr",
  "hr-payroll": "payroll",
  tasks: "tasks",
  meals: "meals",
};
